import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { resolveMascot, type MascotDef } from '../services/mascots';

/**
 * OrgBrandingContext
 *
 * Fetches the signed-in user's organization branding (co-branded logo, name,
 * color, AI-tutor character) once after login and provides it app-wide, so the
 * header can swap the "Level Up Classroom" logo for the org's logo, and the tutor
 * can wear the org's mascot — for teachers AND students.
 */

interface OrgBranding {
  isOrg: boolean;
  domain?: string;
  name?: string;
  logoUrl?: string;
  logoScale?: number;
  color?: string;
  welcomeMessage?: string;
  /** Mascot registry id chosen for this org. Absent for non-org users. */
  tutorMascot?: string;
}

const OrgBrandingContext = createContext<OrgBranding>({ isOrg: false });

export const useOrgBranding = () => useContext(OrgBrandingContext);

/**
 * The AI tutor's character for the signed-in user: their org's, or the house one.
 *
 * Always returns a definition, never undefined, so a component can read
 * `.name`/`.gradient` without guarding. The tutor function resolves the SAME id
 * server-side from the same org record, so what the header says and what the
 * model answers to cannot drift apart.
 */
export const useTutorMascot = (): MascotDef => resolveMascot(useOrgBranding().tutorMascot);

export const OrgBrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [branding, setBranding] = useState<OrgBranding>({ isOrg: false });

  useEffect(() => {
    if (!currentUser) {
      setBranding({ isOrg: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const idToken = await currentUser.getIdToken();
        const res = await fetch('/.netlify/functions/org-branding', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: '{}',
        });
        const data = await res.json().catch(() => ({ isOrg: false }));
        if (!cancelled) setBranding(data?.isOrg ? data : { isOrg: false });
      } catch {
        if (!cancelled) setBranding({ isOrg: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  return <OrgBrandingContext.Provider value={branding}>{children}</OrgBrandingContext.Provider>;
};
